import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FAVORITE_BETS_STORAGE_KEY } from '../utils/favoriteBet';

const FavoriteBetsContext = createContext(null);

function loadStored() {
  try {
    const raw = localStorage.getItem(FAVORITE_BETS_STORAGE_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export function FavoriteBetsProvider({ children }) {
  const [favorites, setFavorites] = useState(() => loadStored());

  useEffect(() => {
    try {
      localStorage.setItem(FAVORITE_BETS_STORAGE_KEY, JSON.stringify(favorites));
    } catch {
      /* ignore quota */
    }
  }, [favorites]);

  const addFavorite = useCallback((item) => {
    if (!item?.id || !item?.label) return false;
    setFavorites((prev) => {
      if (prev.some((x) => x.id === item.id)) return prev;
      return [
        ...prev,
        {
          ...item,
          createdAt: item.createdAt || new Date().toISOString(),
        },
      ];
    });
    return true;
  }, []);

  /** Adiciona várias seleções de uma vez (ignora ids já presentes). */
  const addFavoritesBulk = useCallback((items) => {
    if (!Array.isArray(items) || items.length === 0) return;
    setFavorites((prev) => {
      const ids = new Set(prev.map((x) => x.id));
      const toAppend = [];
      for (const item of items) {
        if (!item?.id || !item?.label) continue;
        if (ids.has(item.id)) continue;
        ids.add(item.id);
        toAppend.push({
          ...item,
          createdAt: item.createdAt || new Date().toISOString(),
        });
      }
      if (toAppend.length === 0) return prev;
      return [...prev, ...toAppend];
    });
  }, []);

  const removeFavorite = useCallback((id) => {
    setFavorites((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const toggleFavorite = useCallback((item) => {
    if (!item?.id) return;
    setFavorites((prev) => {
      if (prev.some((x) => x.id === item.id)) {
        return prev.filter((x) => x.id !== item.id);
      }
      return [...prev, { ...item, createdAt: item.createdAt || new Date().toISOString() }];
    });
  }, []);

  const updateFavorite = useCallback((id, patch) => {
    setFavorites((prev) =>
      prev.map((x) => (x.id === id ? { ...x, ...patch } : x)),
    );
  }, []);

  const clearFavorites = useCallback(() => setFavorites([]), []);

  const isFavorite = useCallback(
    (id) => favorites.some((x) => x.id === id),
    [favorites],
  );

  const value = useMemo(
    () => ({
      favorites,
      addFavorite,
      addFavoritesBulk,
      removeFavorite,
      toggleFavorite,
      updateFavorite,
      clearFavorites,
      isFavorite,
    }),
    [
      favorites,
      addFavorite,
      addFavoritesBulk,
      removeFavorite,
      toggleFavorite,
      updateFavorite,
      clearFavorites,
      isFavorite,
    ],
  );

  return (
    <FavoriteBetsContext.Provider value={value}>
      {children}
    </FavoriteBetsContext.Provider>
  );
}

export function useFavoriteBets() {
  const ctx = useContext(FavoriteBetsContext);
  if (!ctx) {
    throw new Error('useFavoriteBets must be used within FavoriteBetsProvider');
  }
  return ctx;
}
